import React from 'react';
import { connect } from 'react-redux';
import { buildSystemFields, buildSystemCopyText } from '../util/systemDetails';
import { getProductMode, PRODUCT_MODE } from '../backend/productMode';
import { getBackend } from '../backend';
import Sheet from '../common/Sheet';
import './liveStyle.css';

/**
 * SystemInfoSheet — the mobile/tablet "System details" bottom sheet (#86bb5dx62). The touch
 * equivalent of the desktop bottom-bar ⓘ popover (SystemInfoPopover): the SAME field list +
 * Copy-all, both from util/systemDetails, so the two never drift. Opened by the ⓘ in the mobile
 * top bar (portrait) or the landscape rail; dismissed by the scrim or the ✕. Reuses the .live-sheet
 * bottom-sheet chrome (Layout/Views/overlays use it too).
 *
 * Props (own): visible, onClose. Server-slice fields come via connect.
 */
class SystemInfoSheet extends React.Component {
    state = { copied: false };
    componentDidMount() {
        // Watch the desktop boundary so a resize ACROSS it re-evaluates the render gate below (the
        // component won't otherwise re-render on a bare window resize).
        if (typeof window !== 'undefined' && window.matchMedia) {
            this._mql = window.matchMedia('(min-width: 1280px)');
            this._onMq = () => this.forceUpdate();
            if (this._mql.addEventListener) this._mql.addEventListener('change', this._onMq);
            else if (this._mql.addListener) this._mql.addListener(this._onMq);
        }
    }
    componentWillUnmount() {
        if (this._t) clearTimeout(this._t);
        if (this._mql) {
            if (this._mql.removeEventListener) this._mql.removeEventListener('change', this._onMq);
            else if (this._mql.removeListener) this._mql.removeListener(this._onMq);
        }
    }

    copyAll = () => {
        const text = buildSystemCopyText(buildSystemFields(this.props));
        try { if (navigator.clipboard) navigator.clipboard.writeText(text); } catch (e) { /* clipboard blocked — ignore */ }
        this.setState({ copied: true });
        if (this._t) clearTimeout(this._t);
        this._t = setTimeout(() => this.setState({ copied: false }), 900);
    };

    render() {
        if (!this.props.visible) return null;
        // Desktop (>= 1280) shows the anchored SystemInfoPopover (bottom-bar ⓘ), NEVER this mobile bottom
        // sheet. The mobile ⓘ triggers are hidden at desktop, but guard the resize-while-open edge so the
        // full-width sheet can't cover the desktop screen (David 2026-08-14). Below desktop it is the sheet.
        const isDesktop = this._mql ? this._mql.matches
            : (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(min-width: 1280px)').matches);
        if (isDesktop) return null;
        const { onClose } = this.props;
        const { copied } = this.state;
        const fields = buildSystemFields(this.props);
        return (
            <Sheet
                className="live-sheet live-sysinfo-sheet"
                scrimClassName="live-sheet-scrim"
                scrimTestId="sysinfo-scrim"
                title="System details"
                ariaLabel="System details"
                onClose={onClose}
            >
                    <div className="live-sysinfo-sheet__rows">
                        {fields.filter((f) => !f.copyOnly).map((f) => (
                            <div key={f.k} className="live-sysinfo-sheet__row">
                                <span className="live-sysinfo-sheet__l">{f.k}</span>
                                <span className={'live-sysinfo-sheet__v' + (f.muted ? ' live-sysinfo-sheet__v--muted' : '')} title={f.v}>{f.v}</span>
                            </div>
                        ))}
                    </div>
                    <button className={'live-sysinfo-sheet__copy' + (copied ? ' live-sysinfo-sheet__copy--done' : '')} onClick={this.copyAll}>
                        {copied ? 'Copied' : 'Copy all details'}
                    </button>
            </Sheet>
        );
    }
}

const mapStateToProps = (state) => {
    const { sName, fEview, bSerial, activeServerSerial, sVersion, sReleaseVersion, fReleaseCandidate, authServers, usersOnline, machineStatus } = state.server;
    // Cloud (#86bb5dx62): gate the local-only rows + show the cloudapi version (mirrors SystemInfoPopover).
    const isCloud = getProductMode(state) === PRODUCT_MODE.CLOUD;
    return {
        sName, fEview, bSerial, activeServerSerial, sVersion, sReleaseVersion, fReleaseCandidate, authServers, usersOnline, machineStatus,
        controllerSerial: bSerial,                          // the login/controller NVR serial (eView)
        userName: (state.user && state.user.sName) || '',   // logged-in user — copy-only (David)
        isCloud,
        cloudApiVersion: isCloud ? (state.utility.cloudApiVersion || getBackend().getBackendVersion()) : null
    };
};

export { SystemInfoSheet };
export default connect(mapStateToProps)(SystemInfoSheet);
